import { useState, useMemo, useEffect } from 'react';
import { Search, ListChecks, ChevronRight, ArrowRight, Eye } from 'lucide-react';
import {
  getTeamPlayerIds,
  kmSeriesMatchToRow,
  getDisplayTournamentId,
  getDisplayMatchId,
  getTournamentType,
} from '../../engine';
import { highlightMatch } from './highlightMatch';
import { UniversalMatchRow } from './UniversalMatchRow';
import { ArchiveMatchDetailModal } from './ArchiveMatchDetailModal';

// ─────────────────────────────────────────────────────────────────────
// AllMatchesArchive — the "Alle kamper" (All matches) tab in the archive.
// Extracted verbatim from the original monolith (legacy/App.jsx). Flat-maps
// all played matches across all activities in the archive, with search,
// type filter, pagination and contiguous tournament grouping. Renders a
// UniversalMatchRow per match and opens ArchiveMatchDetailModal on click.
//
// Engine swaps (monolith → src/engine named imports, same functions):
//   getTeamPlayerIds / kmSeriesMatchToRow / getDisplayTournamentId /
//   getDisplayMatchId → '../../engine'
//   Padel24Engine.getTournamentType → getTournamentType
//   highlightMatch → './highlightMatch'
// ─────────────────────────────────────────────────────────────────────

export interface AllMatchesArchiveProps {
  archive: any[];
  effectivePlayerDB: any;
  onOpenArchived?: (sourceData: any) => void;
  onPlayerClick?: (p: any) => void;
}

export function AllMatchesArchive({
  archive,
  effectivePlayerDB,
  onOpenArchived,
  onPlayerClick,
}: AllMatchesArchiveProps) {
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'padel24' | 'americano' | 'single_matches'
  const [search, setSearch] = useState('');
  const [selectedMatch, setSelectedMatch] = useState(null); // { match, sourceData, typeInfo, ... }
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set()); // collapsed tournament groups
  const toggleGroup = (gk) =>
    setCollapsedGroups((prev) => {
      const n = new Set(prev);
      if (n.has(gk)) n.delete(gk);
      else n.add(gk);
      return n;
    });

  // Build a global playerById from effectivePlayerDB (can be a map or array)
  const globalPlayerById = useMemo(() => {
    const map = {};
    const src = effectivePlayerDB || {};
    const arr = Array.isArray(src) ? src : Object.values(src);
    arr.forEach((p) => {
      if (p && p.id) map[p.id] = p;
    });
    return map;
  }, [effectivePlayerDB]);

  // Flat-map all matches from the archive
  const allMatches = useMemo(() => {
    const list = [];

    archive.forEach((item) => {
      const t = item.data;
      if (!t || !Array.isArray(t.matches)) return;

      const typeInfo = getTournamentType(t.config);
      const sourceType = t.config?.tournamentType || t.type || 'padel24';
      const sourceName = t.config?.name || 'Aktivitet';
      const archivedAt = t.archivedAt || 0;

      // Build a local playerById that combines the tournament's players
      // with effectivePlayerDB. UniversalMatchRow uses playerEloPre regardless,
      // so the tournament's players snapshot is needed mainly for name/fallback ELO.
      const localPlayerById = { ...globalPlayerById };
      (t.players || []).forEach((p) => {
        if (p && p.id && !localPlayerById[p.id]) {
          localPlayerById[p.id] = p;
        }
      });

      const isKMlike = sourceType === 'klubbmesterskap' || sourceType === 'seriespill';
      t.matches.forEach((m0, idx) => {
        let m = m0;
        if (isKMlike) {
          if (!m0 || !m0.score) return; // KM/seriespill: only matches with a result
          m = kmSeriesMatchToRow(m0, t.pairs);
        } else {
          if (!m0 || !m0.completed) return; // Only played matches
        }

        // Pseudo-timestamp for stable sorting within the same activity
        const phaseNum =
          m.phase === 'phase1' ? 1 : m.phase === 'phase2' ? 2 : m.phase === 'finals' ? 3 : 0;
        const sortKey =
          archivedAt + phaseNum * 100000 + (m.round || 0) * 1000 + (m.court || 0) * 10 + idx;

        // Actual match time if available (single matches have date+timeMinutes)
        let playedAt = archivedAt;
        if (m.date && m.timeMinutes != null) {
          const d = new Date(m.date);
          if (!isNaN(d.getTime())) {
            d.setHours(0, 0, 0, 0);
            playedAt = d.getTime() + (m.timeMinutes || 0) * 60 * 1000;
          }
        }

        list.push({
          id: `${item.key}:${m.id || idx}`,
          match: m,
          typeInfo,
          sourceType,
          sourceName,
          sourceData: t,
          sourceKey: item.key,
          sourceIsVirtual: !!t.isVirtual,
          playerById: localPlayerById,
          archivedAt,
          playedAt,
          sortKey,
        });
      });
    });

    return list;
  }, [archive, globalPlayerById]);

  // Filter + sort
  const filtered = useMemo(() => {
    let list = allMatches;

    if (typeFilter !== 'all') {
      list = list.filter((e) => e.sourceType === typeFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((e) => {
        const [t1p1Id, t1p2Id] = getTeamPlayerIds(e.match.team1);
        const [t2p1Id, t2p2Id] = getTeamPlayerIds(e.match.team2);
        const names = [t1p1Id, t1p2Id, t2p1Id, t2p2Id]
          .map((id) => e.playerById[id]?.name || '')
          .join(' ')
          .toLowerCase();
        if (names.includes(q)) return true;
        if (e.sourceName.toLowerCase().includes(q)) return true;
        return false;
      });
    }

    // Newest first
    return [...list].sort((a, b) => b.sortKey - a.sortKey);
  }, [allMatches, typeFilter, search]);

  // Type filter — only show types present in the dataset (full names in the dropdown)
  const availableTypes = useMemo(() => {
    const types = new Set(allMatches.map((e) => e.sourceType));
    const opts = [{ id: 'all', label: 'Alle typer' }];
    if (types.has('padel24')) opts.push({ id: 'padel24', label: 'Padel24' });
    if (types.has('americano')) opts.push({ id: 'americano', label: 'Americano' });
    if (types.has('single_matches')) opts.push({ id: 'single_matches', label: 'Enkeltkamper' });
    Array.from(types).forEach((t) => {
      if (!['padel24', 'americano', 'single_matches'].includes(t)) {
        const info = getTournamentType({ tournamentType: t });
        opts.push({ id: t, label: info.label || info.abbrev || t });
      }
    });
    return opts;
  }, [allMatches]);

  // Reset to page 1 when filter/search/page size changes
  useEffect(() => {
    setPage(1);
  }, [typeFilter, search, pageSize]);
  // Reset collapsed groups when the list changes (group ids change then)
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [typeFilter, search]);

  // Group CONTIGUOUS matches from the same tournament (order is preserved).
  // Each entry gets a group id; group info holds the count within the filter.
  const { entryGroupKey, groupInfo } = useMemo(() => {
    const keyOf = (e) => e.sourceKey || e.sourceName || '?';
    const egk = [];
    const info = {};
    let curKey = null,
      curId = null;
    filtered.forEach((e, i) => {
      const sk = keyOf(e);
      if (curId === null || sk !== curKey) {
        curKey = sk;
        curId = `${sk}@${i}`; // contiguous chain starts here
      }
      egk[i] = curId;
      if (!info[curId]) {
        info[curId] = {
          count: 0,
          sourceName: e.sourceName,
          typeInfo: e.typeInfo,
          sourceData: e.sourceData,
        };
      }
      info[curId].count++;
    });
    return { entryGroupKey: egk, groupInfo: info };
  }, [filtered]);

  // Pagination: only show the current page (performance with many matches)
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const paged = filtered.slice(pageStart, pageStart + pageSize);

  // Build the card for a single match. `grouped` = part of a collapsible group
  // (the header strip then drops the tournament name/badge since the group bar shows it).
  const renderMatchCard = (entry, grouped) => {
    const dateLabel = entry.playedAt
      ? new Date(entry.playedAt).toLocaleDateString('no-NO', {
          day: '2-digit',
          month: 'short',
          year: '2-digit',
        })
      : '';
    let contextLabel = '';
    let contextSubLabel = '';
    if (entry.sourceType === 'padel24') {
      if (entry.match.court) contextLabel = `Bane ${entry.match.court}`;
      if (entry.match.phase === 'phase1') contextSubLabel = `Fase 1·R${entry.match.round || '?'}`;
      else if (entry.match.phase === 'phase2')
        contextSubLabel = `Fase 2·R${entry.match.round || '?'}`;
      else if (entry.match.phase === 'finals') contextSubLabel = 'Finale';
    } else if (entry.sourceType === 'americano') {
      if (entry.match.isFinal) {
        contextLabel = entry.match.finalNumber ? `Finale ${entry.match.finalNumber}` : 'Finale';
      } else {
        contextLabel = `Runde ${entry.match.round || '?'}`;
      }
      if (entry.match.court) contextSubLabel = `Bane ${entry.match.court}`;
    }
    return (
      <div
        key={entry.id}
        className="card-elev overflow-hidden"
        style={grouped ? { marginLeft: 10, borderLeft: '2px solid var(--line)' } : undefined}
      >
        {/* Header strip: type + activity name (clickable to open the activity) + date.
            In a group only the ID + date are shown (name/badge live in the group bar). */}
        <button
          onClick={() => onOpenArchived?.(entry.sourceData)}
          className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-bg-soft/50 hover:bg-bg-soft transition-colors text-left border-b border-line"
          title={`Åpne ${entry.sourceName}`}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {!grouped && (
              <span
                className="font-mono text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0"
                style={{ background: entry.typeInfo.color + '40', color: '#0f1611' }}
              >
                {entry.typeInfo.abbrev || entry.typeInfo.label}
              </span>
            )}
            {!grouped && (
              <span className="text-sm font-semibold text-ink truncate">
                {highlightMatch(entry.sourceName, search)}
              </span>
            )}
            {!entry.sourceData?.isVirtual && (
              <span
                className="font-mono text-[10px] text-ink-3 flex-shrink-0"
                title="Turnerings- og match-ID"
              >
                {getDisplayTournamentId(entry.sourceData)} ·{' '}
                {getDisplayMatchId(entry.sourceData, entry.match)}
              </span>
            )}
            {!grouped && <ArrowRight size={12} className="text-ink-3 flex-shrink-0 opacity-60" />}
          </div>
          <span className="font-mono text-[10px] text-ink-3 flex-shrink-0 whitespace-nowrap">
            {dateLabel}
          </span>
        </button>

        {/* The match itself — UniversalMatchRow with brief context */}
        <div
          onClick={() => setSelectedMatch(entry)}
          className="cursor-pointer flex items-stretch hover:bg-lime-bg/20 transition-colors"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSelectedMatch(entry);
          }}
          title="Klikk for kampdetaljer"
        >
          <div className="flex-shrink-0 flex items-center justify-center px-3 bg-bg-soft/40 border-r border-line-soft">
            <Eye size={14} className="text-ink-3" />
          </div>
          <div className="flex-1 min-w-0 overflow-x-auto">
            <UniversalMatchRow
              match={entry.match}
              playerById={entry.playerById}
              onPlayerClick={onPlayerClick}
              metaLabel={contextLabel}
              metaSubLabel={contextSubLabel}
              playerSearch={search}
            />
          </div>
          <div className="flex-shrink-0 flex items-center justify-center px-3 text-ink-3">
            <ChevronRight size={16} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Filter row */}
      <div className="flex items-center gap-2 mb-2">
        <div className="relative" style={{ flex: '1 1 100px', minWidth: 0 }}>
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk..."
            className="input w-full text-sm"
            style={{ paddingLeft: '2rem' }}
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="input text-xs py-1.5 pr-7"
          style={{ flex: '0 0 auto', width: 'auto' }}
          title="Filtrer på type"
          aria-label="Filtrer på type"
        >
          {availableTypes.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="input text-xs py-1.5 pr-7"
          style={{ flex: '0 0 auto', width: 'auto' }}
          title="Antall per side"
          aria-label="Antall per side"
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={250}>250</option>
        </select>
      </div>

      <div className="text-[11px] text-ink-3 font-mono mb-3">
        {filtered.length === 0
          ? '0'
          : `${pageStart + 1}–${Math.min(pageStart + pageSize, filtered.length)}`}{' '}
        av {filtered.length} kamper
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="card p-12 text-center text-ink-3">
          <ListChecks size={32} className="mx-auto mb-3 opacity-50" />
          {allMatches.length === 0 ? (
            <>
              <div className="font-display text-lg mb-1">Ingen kamper spilt ennå</div>
              <div className="text-sm">Spilte kamper fra alle aktiviteter vises her</div>
            </>
          ) : (
            <>
              <div className="font-display text-lg mb-1">Ingen treff</div>
              <div className="text-sm">Prøv et annet søk eller filter</div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {(() => {
            const out = [];
            let prevKey = null;
            paged.forEach((entry, j) => {
              const gk = entryGroupKey[pageStart + j];
              const ginfo = groupInfo[gk];
              const isGrouped = !!ginfo && ginfo.count > 1;
              const isNewGroup = gk !== prevKey;
              prevKey = gk;
              const collapsed = isGrouped && collapsedGroups.has(gk);
              // Group bar (only for contiguous chains with more than 1 match)
              if (isGrouped && isNewGroup) {
                out.push(
                  <div
                    key={`grp:${gk}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-soft/70 border border-line"
                  >
                    <button
                      onClick={() => toggleGroup(gk)}
                      className="flex items-center gap-1.5 min-w-0 flex-1 text-left"
                      title={collapsed ? 'Vis kamper' : 'Skjul kamper'}
                    >
                      <ChevronRight
                        size={14}
                        className="text-ink-3 flex-shrink-0"
                        style={{
                          transform: collapsed ? 'none' : 'rotate(90deg)',
                          transition: 'transform .15s',
                        }}
                      />
                      <span
                        className="font-mono text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0"
                        style={{ background: ginfo.typeInfo.color + '40', color: '#0f1611' }}
                      >
                        {ginfo.typeInfo.abbrev || ginfo.typeInfo.label}
                      </span>
                      <span className="text-sm font-semibold text-ink truncate">
                        {highlightMatch(ginfo.sourceName, search)}
                      </span>
                      <span className="font-mono text-[11px] text-ink-3 flex-shrink-0">
                        · {ginfo.count} kamper
                      </span>
                    </button>
                    <button
                      onClick={() => onOpenArchived?.(ginfo.sourceData)}
                      className="text-ink-3 hover:text-ink-1 flex-shrink-0"
                      title={`Åpne ${ginfo.sourceName}`}
                    >
                      <ArrowRight size={14} />
                    </button>
                  </div>,
                );
              }
              // Collapsed group: skip the match cards
              if (collapsed) return;
              out.push(renderMatchCard(entry, isGrouped));
            });
            return out;
          })()}
        </div>
      )}

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            className="text-sm font-semibold px-3 py-1.5 rounded-full border border-line flex items-center gap-1"
            style={{
              opacity: safePage <= 1 ? 0.4 : 1,
              cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Forrige
          </button>
          <span className="text-xs text-ink-2 font-mono px-1">
            Side {safePage} av {pageCount}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={safePage >= pageCount}
            className="text-sm font-semibold px-3 py-1.5 rounded-full border border-line flex items-center gap-1"
            style={{
              opacity: safePage >= pageCount ? 0.4 : 1,
              cursor: safePage >= pageCount ? 'not-allowed' : 'pointer',
            }}
          >
            Neste <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Hint footnote */}
      {filtered.length > 0 && (
        <div className="mt-3 px-3 py-2 text-[11px] text-ink-3 italic">
          Klikk på en rad for kampdetaljer · Klikk på aktivitetsnavnet for å åpne hele aktiviteten ·
          Klikk på et spillernavn for å åpne spillerprofilen
        </div>
      )}

      {/* Detail modal */}
      {selectedMatch && (
        <ArchiveMatchDetailModal
          entry={selectedMatch}
          onClose={() => setSelectedMatch(null)}
          onOpenSource={() => {
            onOpenArchived?.(selectedMatch.sourceData);
            setSelectedMatch(null);
          }}
          onPlayerClick={(p) => {
            onPlayerClick?.(p);
            setSelectedMatch(null);
          }}
        />
      )}
    </>
  );
}
